"use client";

import { useEffect, useState } from "react";

import { useComboInstrumentsController } from "#/hooks/useComboInstruments.controller";
import { useStrikes } from "#/hooks/useStrikes";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import { type OptionType } from "#/types/options";
import { nextMaturities } from "#/utils/maturities";

import {
  findClosestATMStrikeIndex,
  showComboWarningToast,
} from "../StrategySelector.utils";

const findEquidistantStrikes = (
  strikes: number[],
  middleIndex: number,
): [number, number] | null => {
  const middleStrike = strikes[middleIndex];

  for (let i = middleIndex - 1; i >= 0; i--) {
    const lower = strikes[i];
    const lowerDist = middleStrike - lower;

    for (let k = middleIndex + 1; k < strikes.length; k++) {
      const upper = strikes[k];
      const upperDist = upper - middleStrike;

      if (upperDist > lowerDist) break; // No need to check further, strikes are sorted

      if (lowerDist === upperDist) return [strikes[i], strikes[k]];
    }
  }

  return null;
};

export const useButterfly = (optionType: OptionType) => {
  const {
    comboPerpInstruments,
    comboOptionInstruments,
    handleAddLegs,
    buildInstrument,
  } = useComboInstrumentsController();

  const [maturityIndex, setMaturityIndex] = useState(0);

  const maturities = nextMaturities();
  const maturity = maturities[maturityIndex];
  const { strikes } = useStrikes(maturity);
  const { marketIndexPrice } = useMarketIndexPrice();

  // Apply a long butterfly: buy 1 lower, sell 2 middle, buy 1 upper (all same maturity/underlying/optionType)
  // Note: Users can flip directions to create short butterfly (sell-buy-sell pattern)
  const applyButterfly = () => {
    const middleIndex = findClosestATMStrikeIndex(strikes, marketIndexPrice);
    const middleStrike = strikes[middleIndex];

    const equidistantStrikes = findEquidistantStrikes(strikes, middleIndex);
    if (!equidistantStrikes) {
      if (maturityIndex + 1 < maturities.length) {
        setMaturityIndex(maturityIndex + 1);
        return;
      }

      showComboWarningToast();
      return;
    }

    const [lowerStrike, upperStrike] = equidistantStrikes;

    handleAddLegs([
      buildInstrument({
        type: "options",
        side: "buy",
        optionType,
        strike: lowerStrike,
        size: "1",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "sell",
        optionType,
        strike: middleStrike,
        size: "2",
        maturity,
      }),
      buildInstrument({
        type: "options",
        side: "buy",
        optionType,
        strike: upperStrike,
        size: "1",
        maturity,
      }),
    ]);
  };

  // When maturityIndex changes, try again (except on initial render)
  useEffect(() => {
    if (maturityIndex > 0) {
      applyButterfly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only rerun when maturityIndex changes
  }, [maturityIndex]);

  const isActivePatternButterfly = () => {
    if (comboPerpInstruments.length > 0 || comboOptionInstruments.length !== 3)
      return false;

    const [a, b, c] = comboOptionInstruments.toSorted(
      (x, y) => x.strike - y.strike,
    );

    if (
      a.maturity !== b.maturity ||
      b.maturity !== c.maturity ||
      a.underlying !== b.underlying ||
      b.underlying !== c.underlying ||
      a.optionType !== optionType ||
      b.optionType !== optionType ||
      c.optionType !== optionType
    ) {
      return false;
    }

    const isEquidistant = b.strike - a.strike === c.strike - b.strike;
    // Supports both long butterfly (buy-sell-buy) and short butterfly (sell-buy-sell)
    // a.size === c.size, b.size === 2 * a.size
    const aSize = Number(a.size);
    const bSize = Number(b.size);
    const cSize = Number(c.size);
    const isCorrectSidesAndSizes =
      a.side !== b.side &&
      b.side !== c.side &&
      aSize === cSize &&
      bSize === 2 * aSize;

    return isEquidistant && isCorrectSidesAndSizes;
  };

  return { applyButterfly, isActivePatternButterfly };
};
